import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { checkFirstTimeUseEmail, isEmailNeedRecaptchaV2, signUpWithoutPassword } from '@/api/customer';
import { LoginSessionstoragekey } from '@/constants/account';
import { AUTH_SIGN } from '@/constants/auth_sign';
import Constants from '@/constants/const';
import { PHONE_VALID_REG } from '@/constants/reg_rules';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { afterUserLogin, afterUserRegisterLogin, afterUserSign } from '@/utils/common/pixel';
import { getInviterParams, getReferralIdFromCookie } from '@/utils/common/referral';
import { getCookie } from '@/utils/cookie';
import { updateFinger } from '@/utils/finger';
import md5 from '@/utils/md5';
import { formateAmericaPhone } from '@/utils/phoneformat';
import { loadGoogloRecaptcha } from '@/utils/recaptcha';
import { getSafeRedirectUrl } from '@/utils/safeRedirect';
import { isMobileBrowser } from '@/utils/common/device';
import { checkValidityEmailStrict } from '@/utils/common/user';
import { hideLoading, showLoading } from '@/components/common/Indicator/pageIndicator';
import { LoginMethod } from '@/types/account.d';
import { getAccountAuthSource } from '@/utils/accountAuthSource';

import type { LoginFieldType } from './types';

const EMAIL_REG = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

function getLoginSourceKey(): string {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  if (pathname.includes('/account/referral/landing') || pathname.includes('/order/share/grocery/sign-up')) {
    return 'activity';
  }
  return 'newFlow';
}

export type UseLoginFormProps = {
  onSuccess?: (value?: any, arg?: any) => void;
  redirect?: string;
  /** When URL does not specify tab / phone / force_method, use this instead of email */
  defaultActiveLoginMethod?: LoginFieldType;
};

export function useLoginForm(props: UseLoginFormProps) {
  const { onSuccess, redirect: redirectProp, defaultActiveLoginMethod } = props;
  const router = useRouter();
  const { query } = router;
  const { lang } = useTranslation();
  const redirectUrl = redirectProp ?? getSafeRedirectUrl((query?.redirect_url as string) ?? '');
  const accountAuthSource = getAccountAuthSource(query?.source, router.pathname);

  const [activeLoginMethod, setActiveLoginMethod] = useState<LoginFieldType>(() => defaultActiveLoginMethod ?? 'email');
  const [email, setEmail] = useState('');
  const [phoneNum, setPhoneNum] = useState('');
  const [errorRes, setErrorRes] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('1');
  const recaptchaRef = useRef<{
    visible?: boolean;
    show?: () => void;
    hide?: () => void;
    getToken?: () => string;
    reset?: () => void;
  }>(null);
  const { buttonClick } = useClickActionT2();

  const sendEpPartner = useMemo(() => ({ [Constants.EP_PARTNER]: getCookie(Constants.EP_PARTNER) || '' }), []);
  const sendParams = useMemo(() => getInviterParams(), []);

  const handleRedirect = (url: string) => {
    if (isMobileBrowser()) {
      router.push(url);
    } else {
      window.location.href = url;
    }
  };

  useEffect(() => {
    const forceMethod = (query?.force_method as string) || '';
    const isPhone = forceMethod === LoginMethod.PHONE;
    const urlPhone = query?.phone ?? '';
    const key = isPhone ? 2 : urlPhone ? 2 : 1;
    const tabFromQuery = Object.keys(query).includes('tab') ? +(query?.tab as string) : null;
    const fallback = defaultActiveLoginMethod ?? 'email';
    if (tabFromQuery === 2) {
      setActiveLoginMethod('phone');
    } else if (tabFromQuery === 1) {
      setActiveLoginMethod('email');
    } else if (key === 2) {
      setActiveLoginMethod('phone');
    } else {
      setActiveLoginMethod(fallback);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only URL tab + optional default

  useEffect(() => {
    const continueWithEmail = decodeURIComponent((query?.continue_with_e as string) ?? '');
    if (continueWithEmail && checkValidityEmailStrict(continueWithEmail)) {
      setTimeout(() => setEmail(continueWithEmail), 300);
    }
  }, [query]);

  useEffect(() => {
    setPhoneNum('');
    setEmail('');
    setErrorRes('');
    if (recaptchaRef.current?.visible) recaptchaRef.current?.hide?.();
  }, [activeLoginMethod]);

  useEffect(() => {
    loadGoogloRecaptcha().then(token => setRecaptchaToken(token || '1'));
  }, []);

  const validateEmailFormat = (value: string): boolean => EMAIL_REG.test((value || '').trim());
  const validatePhoneFormat = (value: string): boolean => PHONE_VALID_REG.test((value || '').replace(/\D/g, ''));

  const sendVerifyCode = (e?: React.MouseEvent) => {
    e?.preventDefault();
    const phone = phoneNum.replace(/\D/g, '');
    if (!phone || phone.length !== 10) {
      setErrorRes(''); // will be set by caller with t('address_err_msg_phone')
      return false;
    }
    if (!PHONE_VALID_REG.test(phone)) {
      return false;
    }
    const tab_nm = typeof location !== 'undefined' && location.pathname.includes('login') ? 'login' : 'signup';
    if (
      typeof location !== 'undefined' &&
      (location.pathname.includes('login') || location.pathname.includes('signup'))
    ) {
      buttonClick({ co: { target_nm: tab_nm }, ctx: { page_tab: 'phone' } });
    }
    const sourceQuery = accountAuthSource ? `&source=${encodeURIComponent(accountAuthSource)}` : '';
    const url = `/${lang}/account/onboarding/phone-verify?phone=${phone}&redirect_url=${encodeURIComponent(
      redirectUrl
    )}&tab=${tab_nm}${sourceQuery}`;
    handleRedirect(url);
    return true;
  };

  const validateEmail = async () => {
    const value = (email || '').trim();
    if (!value) return;
    if (!validateEmailFormat(value)) return;

    showLoading();
    if (
      typeof location !== 'undefined' &&
      (location.pathname.includes('login') || location.pathname.includes('signup'))
    ) {
      buttonClick({
        co: { target_nm: location.pathname.includes('login') ? 'login' : 'signup' },
        ctx: { page_tab: 'email' }
      });
    }

    const _recaptchaVisible = recaptchaRef.current?.visible;
    if (!_recaptchaVisible) {
      try {
        const checkRecaptchaRes = await isEmailNeedRecaptchaV2({ value }, { headers: { source: getLoginSourceKey() } });
        if (checkRecaptchaRes?.result && checkRecaptchaRes?.object?.check_result) {
          hideLoading();
          recaptchaRef.current?.show?.();
          return;
        }
      } catch {
        hideLoading();
        return;
      }
    }

    const recaptchaV2Token = _recaptchaVisible ? recaptchaRef.current?.getToken?.() ?? '' : '';
    // 已弹出 V2 校验但用户尚未勾选完成时，避免用空 token 直接调 check 接口
    if (_recaptchaVisible && !recaptchaV2Token) {
      hideLoading();
      return;
    }
    let checkRes: any;
    try {
      checkRes = await checkFirstTimeUseEmail(
        { value, recaptcha_response: recaptchaV2Token },
        { headers: { source: getLoginSourceKey() } }
      );
      if (checkRes.result) {
        hideLoading();
        if (recaptchaV2Token) recaptchaRef.current?.hide?.();
        if (checkRes.object.is_first_time_user) {
          // 存储 login key 到 sessionStorage, 用于后续验证码发送,这里跳过了 check-email 流程
          window.sessionStorage?.setItem?.(LoginSessionstoragekey.login_key, checkRes?.object?.code as string);
          handleRedirect(
            `/${lang}/account/verify?email=${encodeURIComponent(value)}&redirect_url=${encodeURIComponent(redirectUrl)}`
          );
          return;
        }
        if (checkRes.object.check_email) {
          localStorage.setItem(Constants.POPUP_CREATE_ACCOUNT_EMAIL, value);
          try {
            await updateFinger();
            const timeStamp = Math.floor(Date.now() / 1000);
            const sign = md5(`${value}${getCookie(Constants.B_COOKIE)}${timeStamp}${AUTH_SIGN}`);
            const res = await signUpWithoutPassword(
              {
                email: value,
                from: 'first_time_use',
                timestamp: timeStamp,
                source: accountAuthSource || getLoginSourceKey(),
                referral_id: getReferralIdFromCookie(),
                recaptcha_response: recaptchaToken,
                ...(redirectUrl ? { page_url: redirectUrl } : {}),
                ...sendEpPartner,
                ...sendParams
              },
              { headers: { sign } }
            );
            buttonClick({
              co: { target_nm: location.pathname.includes('login') ? 'login' : 'signup', click_type: 'complete' },
              ctx: { page_tab: 'email', result: res }
            });
            if (res.result) {
              afterUserRegisterLogin(res?.object?.token, res?.object?.language);
              if (res?.object?.is_sign_up) afterUserSign('email');
              else afterUserLogin('email');
              if (res?.object?.is_edu_email_check) {
                handleRedirect(
                  `/${lang}/student-discount/email-send?email=${encodeURIComponent(
                    value
                  )}&redirect_url=${encodeURIComponent(redirectUrl)}`
                );
                return;
              }
              onSuccess?.({ email: value }, res?.object);
            } else {
              if (res?.message_id === 'CSM10026' || res?.message_id === 'CSM10031') {
                let addRedirectUrl = redirectUrl ? `&redirect_url=${encodeURIComponent(redirectUrl)}` : '';
                window.sessionStorage?.setItem?.(LoginSessionstoragekey.login_key, res?.message as string);
                let _url = `/${lang}/account/verify?email=${encodeURIComponent(value)}${addRedirectUrl}`;
                if (res?.message_id === 'CSM10031') _url += '&type=signup';
                handleRedirect(_url);
                return;
              }
              setErrorRes(res?.message ?? '');
            }
          } catch (err: any) {
            setErrorRes(checkRes?.message ?? err?.message ?? '');
          }
        } else {
          window.pixelLogin?.();
          handleRedirect(
            `/${lang}/account/onboarding/login-with-password?email=${encodeURIComponent(
              value
            )}&redirect_url=${encodeURIComponent(redirectUrl)}`
          );
        }
      } else {
        if (checkRes.message_id === 'CSM30018') recaptchaRef.current?.reset?.();
        hideLoading();
        setErrorRes(checkRes?.message ?? '');
      }
    } catch (err: any) {
      hideLoading();
      setErrorRes(checkRes?.message ?? err?.message ?? '');
    }
  };

  const handleNext = async (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    setErrorRes('');
    await updateFinger();
    if (activeLoginMethod === 'email') {
      const value = (email || '').trim();
      if (!value) return;
      if (!validateEmailFormat(value)) return;
      await validateEmail();
    } else {
      const phone = phoneNum.replace(/\D/g, '');
      if (!phone || phone.length !== 10 || !PHONE_VALID_REG.test(phone)) return;
      sendVerifyCode(e);
    }
  };

  /** 用户输入时：只保留数字并截断为 10 位，供提交/校验使用 */
  const formateAmericaPhoneToNum = (input: string) => {
    const digitsOnly = (input || '').replace(/\D/g, '');
    const tenDigits = digitsOnly.slice(0, 10);
    setPhoneNum(tenDigits);
  };
  /** 展示用：将 10 位数字格式化为 (XXX) XXX-XXXX */
  const phoneDisplayValue = formateAmericaPhone(phoneNum);

  const switchLoginMethod = () => {
    setActiveLoginMethod(prev => (prev === 'email' ? 'phone' : 'email'));
    setErrorRes('');
    if (recaptchaRef.current?.visible) recaptchaRef.current?.hide?.();
  };

  return {
    activeLoginMethod,
    setActiveLoginMethod: (v: LoginFieldType) => {
      setActiveLoginMethod(v);
      setErrorRes('');
    },
    email,
    setEmail: (v: string) => {
      setEmail(v);
      setErrorRes('');
    },
    phoneNum,
    setPhoneNum: formateAmericaPhoneToNum,
    errorRes,
    setErrorRes,
    handleNext,
    validateEmail,
    onRecaptchaSuccess: validateEmail,
    sendVerifyCode,
    recaptchaRef,
    recaptchaToken,
    validateEmailFormat,
    validatePhoneFormat,
    switchLoginMethod,
    redirectUrl,
    lang,
    phoneDisplayValue
  };
}
